import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";

import PageForm from "../../templates/PageForm";

const API_URL = process.env.REACT_APP_CONTRACTS_MANAGEMENT_API_URL;
const API_KEY = process.env.REACT_APP_CONTRACTS_MANAGEMENT_API_KEY;
const API_KEY_PERSON = process.env.REACT_APP_API_KEY;
const BACKOFFICE_URL = process.env.REACT_APP_BACKOFFICE_URL;
const PERSON_SERVICE_URL = process.env.REACT_APP_PERSON_API_URL;
const CONTRACTS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

interface CreditOperationFormData {
    // CreditOperation fields
    operation_number: string;

    // ReceivableAssignment fields
    with_recourse: boolean;
    assignor: string;

    // AssignmentType fields
    assignment_type_name: string;
}

const OperationReceivablesAssignmentFormPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const operationType = searchParams.get('operation_type');
    const [isLoading, setIsLoading] = useState(false);
    const [cedentes, setCedentes] = useState<Array<any>>([]);
    const [searchResults, setSearchResults] = useState<Array<any>>([]);
    const [searchCedente, setSearchCedente] = useState('');
    const [selectedCedente, setSelectedCedente] = useState('');
    const [selectedCedenteName, setSelectedCedenteName] = useState('');
    const [CONTRACT_TEMPLATE_ID, setCONTRACT_TEMPLATE_ID] = useState<number | null>(null);



    const [formData, setFormData] = useState<CreditOperationFormData>({
        // CreditOperation fields
        operation_number: '',

        // ReceivableAssignment fields
        with_recourse: false,
        assignor: '',

        // AssignmentType fields
        assignment_type_name: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Buscar template de contrato
                const templateResponse = await fetch(`${BACKOFFICE_URL}/services/contract-template/receivables-assignment`);
                if (templateResponse.ok) {
                    const templateData = await templateResponse.json();
                    setCONTRACT_TEMPLATE_ID(templateData.id);
                }
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            }
        };
        fetchData();
    }, []);

    const getOperationTypeName = (type: string | null): string => {
        switch (type) {
            case 'RECEIVABLES_ASSIGNMENT': return 'Cessão de recebíveis';
            case 'STRUCTURED_OPERATION': return 'Operação estruturada';
            case 'ACKNOWLEDGEMENT_DEBT': return 'Confissão de dívidas';
            default: return 'Tipo não definido';
        }
    };

    const handlePersonSearch = async (searchValue: string) => {
        setSearchCedente(searchValue);
        if (searchValue.length >= 2) {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());

                const response = await fetch(`${PERSON_SERVICE_URL}/person/ISSUER/${searchValue}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY_PERSON || '',
                        'Content-Type': 'application/json'
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    const results = data.results || [];
                    setSearchResults(results.slice(0, 10));
                    setCedentes(results);
                }
            } catch (error) {
                console.error('Erro ao buscar pessoa:', error);
            }
        } else {
            setSearchResults([]);
        }
    };

    const handleSelectCedente = (cedente: any) => {
        setSelectedCedente(cedente.uuid);
        const cnpjDoc = cedente.documents?.find((doc: any) => doc.type === 'CNPJ');
        const cpfDoc = cedente.documents?.find((doc: any) => doc.type === 'CPF');
        const document = cnpjDoc?.number || cpfDoc?.number || '';
        setSelectedCedenteName(`${document} - ${cedente.name}`);
        setFormData({ ...formData, assignor: cedente.name });
        setSearchResults([]);
        setSearchCedente('');
    };

    const validateForm = (): boolean => {
        if (!formData.assignment_type_name) {
            toast.error('Nome do Tipo de Cessão é obrigatório');
            return false;
        }
        if (!selectedCedente) {
            toast.error('Cedente é obrigatório');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        if (!API_URL || !API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        setIsLoading(true);

        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const selectedCedenteData = cedentes.find(c => c.uuid === selectedCedente);

            const getOperationSchemaId = (type: string | null): number | null => {
                switch (type) {
                    case 'RECEIVABLES_ASSIGNMENT': return 2;
                    case 'STRUCTURED_OPERATION': return 3;
                    case 'ACKNOWLEDGEMENT_DEBT': return 4;
                    default: return null;
                }
            };

            const payload = {
                // CreditOperation fields
                operation_type: operationType,
                operation_number: formData.operation_number,
                operation_schema_id: getOperationSchemaId(operationType),
                status: 1,

                // ContractBuilt fields
                contract_built_status: 1,
                register_status: null,
                contract_template_id: CONTRACT_TEMPLATE_ID,

                // ReceivableAssignment fields
                with_recourse: formData.with_recourse,
                participants: [
                    {
                        person_id: selectedCedenteData.uuid,
                        participant_type: "ASSIGNOR",
                        is_primary: true,
                        name: selectedCedenteData.name,
                        document: selectedCedenteData.documents?.find((doc: any) => doc.type === 'CNPJ')?.number || selectedCedenteData.documents?.find((doc: any) => doc.type === 'CPF')?.number,
                    },
                ],
                // AssignmentType fields
                assignment_type_name: formData.assignment_type_name
            };

            const response = await fetch(`${API_URL}/contracts/receivables_assignment/save`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY || ''
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success('Operação de cessão de recebíveis criada com sucesso!');
                navigate(CONTRACTS_LIST_URL);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao salvar operação');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Erro ao conectar com o servidor');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageForm
            pageTitle="Nova Operação"
            apiVersion=""
            isLoading={isLoading}
            breadcrumb={[
                { label: 'Contratos', url: CONTRACTS_LIST_URL },
                { label: 'Nova operação', url: '#' }
            ]}
            controlSlot={(
                <div className="pt-4 flex justify-end">
                    <button
                        onClick={() => navigate(CONTRACTS_LIST_URL)}
                        disabled={isLoading}
                        className="text-white bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            )}
        >
            <div className="container mx-auto px-2 py-4">
                <h3 className="text-lg font-semibold mb-4">Dados da Operação</h3>
                <div className="mb-6">
                    <div className="text-lg font-medium">
                        <span className="text-gray-600">Tipo de Operação:</span> {getOperationTypeName(operationType)}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <TextEdit
                        label="Número da Operação"
                        value={formData.operation_number}
                        onChange={(e) => setFormData({ ...formData, operation_number: e.target.value })}
                    />
                    <SelectTextField
                        label="Nome do Tipo de Cessão"
                        value={formData.assignment_type_name}
                        items={[
                            { value: 'FACTORING', label: 'Factoring de recebíveis' },
                            { value: 'DISCOUNT', label: 'Desconto de duplicatas' },
                            { value: 'ENDORSEMENT', label: 'Endosso de títulos' }
                        ]}
                        onChange={(e) => setFormData({ ...formData, assignment_type_name: e.target.value })}
                    />
                    <div className="flex items-center pt-6">
                        <input
                            type="checkbox"
                            id="with_recourse"
                            checked={formData.with_recourse}
                            onChange={(e) => setFormData({ ...formData, with_recourse: e.target.checked })}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <label htmlFor="with_recourse" className="ml-2 text-sm font-medium text-gray-900">
                            Com Regresso
                        </label>
                    </div>
                </div>
                <div className="mb-6">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Cedente Selecionado
                        </label>
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100">
                            {selectedCedenteName || 'Nenhum cedente selecionado'}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                            <div className="w-2/5">
                                <TextEdit
                                    label="Buscar Cedente"
                                    value={searchCedente}
                                    onChange={(e) => handlePersonSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100" style={{ width: '15%' }}>Documento</th>
                                        <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100">Nome</th>
                                        <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100">Tipo</th>
                                        <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100" style={{ width: '10%' }}>Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: 10 }, (_, index) => {
                                        const cedente = searchResults[index];
                                        if (cedente) {
                                            const cnpjDoc = cedente.documents?.find((doc: any) => doc.type === 'CNPJ');
                                            const cpfDoc = cedente.documents?.find((doc: any) => doc.type === 'CPF');
                                            const document = cnpjDoc?.number || cpfDoc?.number || '';
                                            const docType = cnpjDoc ? 'CNPJ' : cpfDoc ? 'CPF' : '';
                                            return (
                                                <tr
                                                    key={cedente.uuid}
                                                    className="border-b border-gray-200 dark:border-gray-600"
                                                >
                                                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100" style={{ width: '15%' }}>{document}</td>
                                                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{cedente.name}</td>
                                                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{docType}</td>
                                                    <td className="px-4 py-3 text-left" style={{ width: '10%' }}>
                                                        <button
                                                            onClick={() => handleSelectCedente(cedente)}
                                                            className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-xs px-3 py-1.5 text-center"
                                                        >
                                                            Selecionar
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        } else {
                                            return (
                                                <tr key={index} className="border-b border-gray-200 dark:border-gray-600">
                                                    <td className="px-4 py-3 text-gray-400 dark:text-gray-500" style={{ width: '15%' }}>-</td>
                                                    <td className="px-4 py-3 text-gray-400 dark:text-gray-500">-</td>
                                                    <td className="px-4 py-3 text-gray-400 dark:text-gray-500">-</td>
                                                    <td className="px-4 py-3 text-gray-400 dark:text-gray-500 text-left" style={{ width: '10%' }}>-</td>
                                                </tr>
                                            );
                                        }
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </PageForm>
    );
};

export default OperationReceivablesAssignmentFormPage;